<template>
    <div class="enquiries-form">
        <!-- Success Message -->
        <div v-if="showSuccessMessage" class="alert alert-success" role="alert">
            <i class="fas fa-check-circle"></i>
            {{ successMessage }}
        </div>

        <!-- Error Message -->
        <div v-if="showErrorMessage" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle"></i>
            {{ errorMessage }}
        </div>

        <!-- Contact Form -->
        <div v-if="!showSuccessMessage">
            <!-- Name field (optional) -->
            <div class="form-group">
                <label for="contact-name">Your Name (optional)</label>
                <input
                    id="contact-name"
                    v-model="contactName"
                    type="text"
                    class="form-control"
                    placeholder="Enter your name..."
                    :disabled="isSubmitting"
                />
            </div>

            <!-- Email field (required) -->
            <div class="form-group">
                <label for="contact-email">Your Email Address *</label>
                <input
                    id="contact-email"
                    v-model="contactEmail"
                    type="email"
                    class="form-control"
                    placeholder="Enter your email address..."
                    :disabled="isSubmitting"
                    required
                />
            </div>

            <div class="form-group">
                <label for="contact-message">Your Message</label>
                <textarea
                    id="contact-message"
                    v-model="contactMessage"
                    class="form-control"
                    rows="3"
                    placeholder="Type your question or message here..."
                    :disabled="isSubmitting"
                ></textarea>
            </div>

            <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">
                    <i class="fas fa-clock"></i>
                    The organiser will get back to you as soon as possible.  For quick answers, please use our smart FAQs
                </small>

                <button
                    type="button"
                    class="btn btn-primary"
                    @click="submitContactForm"
                    :disabled="!canSubmit || isSubmitting"
                >
                    <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-paper-plane"></i>
                    {{ isSubmitting ? 'Sending...' : 'Send Message' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EnquiriesForm',

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        }
    },

    data() {
        return {
            contactMessage: '',
            contactName: '',
            contactEmail: '',
            isSubmitting: false,
            showSuccessMessage: false,
            successMessage: '',
            showErrorMessage: false,
            errorMessage: ''
        }
    },

    computed: {
        canSubmit() {
            if (!this.contactMessage.trim()) {
                return false;
            }

            // Always require email
            if (!this.contactEmail.trim()) {
                return false;
            }

            return true;
        }
    },

    methods: {
        async submitContactForm() {
            if (!this.contactMessage.trim()) return

            this.isSubmitting = true

            try {
                // Send enquiry via API
                const enquiryData = {
                    message: this.contactMessage.trim(),
                    occasion_id: this.occasionId
                };

                // Always add contact details
                if (this.contactName.trim()) {
                    enquiryData.contact_name = this.contactName.trim();
                }
                enquiryData.contact_email = this.contactEmail.trim();

                const response = await this.sendEnquiry(enquiryData)

                // Emit success event
                this.$emit('contact-submitted', {
                    message: this.contactMessage.trim(),
                    occasionId: this.occasionId,
                    response: response.data
                })

                // Reset form
                this.contactMessage = ''
                this.contactName = ''
                this.contactEmail = ''

                // Show success message
                this.successMessage = response.data.message || 'Your enquiry has been sent successfully!'
                this.showSuccessMessage = true

                // Hide success message after 5 seconds
                setTimeout(() => {
                    this.showSuccessMessage = false
                }, 5000)

                // Also emit event for parent components
                this.$emit('contact-success', this.successMessage)

            } catch (error) {
                console.error('Contact form submission error:', error)

                let errorMessage = 'Failed to send your enquiry. Please try again.'

                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message
                } else if (error.message) {
                    errorMessage = error.message
                }

                // Show error message to user
                this.errorMessage = errorMessage
                this.showErrorMessage = true

                // Hide error message after 5 seconds
                setTimeout(() => {
                    this.showErrorMessage = false
                }, 5000)

                this.$emit('contact-error', {
                    message: errorMessage,
                    originalError: error
                })
            } finally {
                this.isSubmitting = false
            }
        },

        async sendEnquiry(data) {
            // Get CSRF token if available
            const csrfTokenElement = document.querySelector('meta[name="csrf-token"]')
            const csrfToken = csrfTokenElement ? csrfTokenElement.getAttribute('content') : null

            const headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }

            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken
            }

            const response = await fetch('/api/v0/enquiries/send', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(data)
            })

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`

                try {
                    const errorText = await response.text()
                    console.error('API Error Response:', errorText)

                    // Try to parse as JSON first
                    try {
                        const errorData = JSON.parse(errorText)
                        errorMessage = errorData.message || errorMessage
                    } catch (jsonError) {
                        // If not JSON, check if it's HTML error page
                        if (errorText.includes('<html>') || errorText.includes('<!DOCTYPE')) {
                            errorMessage = 'Server error occurred. Please try again later.'
                        } else {
                            errorMessage = errorText.substring(0, 200) // Truncate long error messages
                        }
                    }
                } catch (textError) {
                    console.error('Failed to read error response:', textError)
                }

                throw new Error(errorMessage)
            }

            const responseText = await response.text()
            console.log('API Response:', responseText)

            let result
            try {
                result = JSON.parse(responseText)
            } catch (parseError) {
                console.error('Failed to parse response as JSON:', responseText)
                throw new Error('Invalid response format from server')
            }

            if (!result.data || !result.data.success) {
                throw new Error((result.data && result.data.message) || result.message || 'Unknown error occurred')
            }

            return result
        }
    }
}
</script>

<style scoped>
/* Form styling */
.form-group label {
    font-weight: 500;
    color: #495057;
}

/* Responsive adjustments */

    .d-flex {
        flex-direction: column;
        gap: 0.75rem;
    }

    .d-flex .btn {
        width: 100%;
    }
</style>



// WEBPACK FOOTER //
// src/Assets/components/Enquiries/EnquiriesForm.vue